<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader title="Super Admin Dashboard" title1="Manage platform tenants and users" :items="[]" />

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <div class="admin-kpi-grid">
          <div v-for="card in cards" :key="card.label" class="metric-card" :class="card.tone">
            <div class="metric-icon">
              <vue-feather :type="card.icon"></vue-feather>
            </div>
            <div class="metric-content">
              <span class="metric-label">{{ card.label }}</span>
              <strong class="metric-value">{{ loading ? '-' : card.value }}</strong>
              <small class="metric-note">{{ card.note }}</small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-xl-8 col-lg-12">
            <div class="card">
              <div class="card-body">
                <div class="table-top">
                  <div class="search-set">
                    <div class="dashboard-title">
                      <span class="head-icon"><vue-feather type="activity"></vue-feather></span>
                      <div>
                        <h4>Platform Summary</h4>
                        <p>Tenant lifecycle, limits and user growth across the installation.</p>
                      </div>
                    </div>
                  </div>
                  <div class="wordset">
                    <ul>
                      <li>
                        <router-link class="btn btn-added" :to="{ name: 'admin-tenants' }">
                          <img src="../../../assets/img/icons/eye.svg" class="me-2" alt="view" />
                          View Tenants
                        </router-link>
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="summary-grid">
                  <div v-for="item in summaryItems" :key="item.label" class="summary-item">
                    <span>{{ item.label }}</span>
                    <strong>{{ loading ? '-' : item.value }}</strong>
                    <small>{{ item.note }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-xl-4 col-lg-12">
            <div class="card">
              <div class="card-body">
                <div class="dashboard-title mb-3">
                  <span class="head-icon warn"><vue-feather type="shield"></vue-feather></span>
                  <div>
                    <h4>Admin Tools</h4>
                    <p>Quick paths for platform administration.</p>
                  </div>
                </div>
                <div class="quick-actions">
                  <router-link :to="{ name: 'admin-tenants' }">
                    <vue-feather type="briefcase"></vue-feather>
                    <span>Manage Tenants</span>
                  </router-link>
                  <button type="button" @click="fetchDashboard" :disabled="loading">
                    <vue-feather type="refresh-cw"></vue-feather>
                    <span>{{ loading ? 'Refreshing...' : 'Refresh Data' }}</span>
                  </button>
                  <button type="button" @click="openSystemUserModal">
                    <vue-feather type="user-plus"></vue-feather>
                    <span>Add Internal User</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showSystemUserModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <form @submit.prevent="submitSystemUser">
                <div class="modal-header">
                  <h5 class="modal-title">Add System Internal User</h5>
                  <button type="button" class="btn-close" aria-label="Close" @click="closeSystemUserModal"></button>
                </div>
                <div class="modal-body">
                  <div v-if="systemUserError" class="alert alert-danger">{{ systemUserError }}</div>

                  <div class="row g-3">
                    <div class="col-12">
                      <label class="form-label">Tenant</label>
                      <select
                        v-model="systemUserForm.tenant_id"
                        class="form-control"
                        required
                        @change="loadTenantLocations"
                      >
                        <option value="">Select tenant</option>
                        <option v-for="tenant in activeTenants" :key="tenant.tenantid" :value="tenant.tenantid">
                          {{ tenant.tenantname }}
                        </option>
                      </select>
                    </div>

                    <div class="col-12">
                      <label class="form-label">Default Location</label>
                      <select
                        v-model="systemUserForm.locationid"
                        class="form-control"
                        :disabled="tenantLocationsLoading || !tenantLocations.length"
                      >
                        <option value="">Use tenant default location</option>
                        <option v-for="location in tenantLocations" :key="location.locationid" :value="location.locationid">
                          {{ location.locationname }}
                        </option>
                      </select>
                    </div>

                    <div class="col-12">
                      <label class="form-label">Name</label>
                      <input v-model="systemUserForm.name" type="text" class="form-control" required />
                    </div>

                    <div class="col-12">
                      <label class="form-label">Email</label>
                      <input v-model="systemUserForm.email" type="email" class="form-control" required />
                    </div>

                    <div class="col-12">
                      <label class="form-label">Password</label>
                      <input v-model="systemUserForm.password" type="password" class="form-control" required minlength="6" />
                    </div>

                    <div class="col-12">
                      <label class="form-label">Confirm Password</label>
                      <input v-model="systemUserForm.confirmPassword" type="password" class="form-control" required minlength="6" />
                    </div>

                    <div class="col-sm-6 col-12">
                      <label class="form-label d-block">Active</label>
                      <input v-model="systemUserForm.is_active" type="checkbox" />
                    </div>

                    <div class="col-sm-6 col-12">
                      <label class="form-label d-block">Force Password Change</label>
                      <input v-model="systemUserForm.must_change_password" type="checkbox" />
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-cancel" @click="closeSystemUserModal">Cancel</button>
                  <button type="submit" class="btn btn-submit" :disabled="systemUserSubmitting">
                    {{ systemUserSubmitting ? 'Creating...' : 'Create User' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div v-if="showSystemUserModal" class="modal-backdrop fade show"></div>

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-set">
                <div class="dashboard-title">
                  <span class="head-icon"><vue-feather type="grid"></vue-feather></span>
                  <div>
                    <h4>Operations</h4>
                    <p>Superadmin controls use tenant-scoped records and audit logging.</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="platform-card">
                  <span><vue-feather type="briefcase"></vue-feather></span>
                  <div>
                    <h6>Tenant Management</h6>
                    <p>Create companies, default locations, company settings and first admin users.</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="platform-card">
                  <span><vue-feather type="shield"></vue-feather></span>
                  <div>
                    <h6>Super Admin Access</h6>
                    <p>Only users with no tenant and the superadmin role can open these screens.</p>
                  </div>
                </div>
              </div>
              <div class="col-lg-4 col-sm-6 col-12">
                <div class="platform-card">
                  <span><vue-feather type="activity"></vue-feather></span>
                  <div>
                    <h6>Activity</h6>
                    <p>Tenant create, edit, suspend and activate actions are written to audit logs.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

import { createSystemInternalUser, getSuperAdminDashboard, getTenantDetails, listTenants } from '@/services/superAdmin';

const emptySystemUserForm = () => ({
  tenant_id: '',
  locationid: '',
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  is_active: true,
  must_change_password: true,
});

export default {
  name: 'SuperAdminDashboard',
  data() {
    return {
      loading: false,
      error: '',
      tenants: [],
      tenantLocations: [],
      tenantLocationsLoading: false,
      showSystemUserModal: false,
      systemUserSubmitting: false,
      systemUserError: '',
      systemUserForm: emptySystemUserForm(),
      stats: {
        total_tenants: 0,
        active_tenants: 0,
        suspended_tenants: 0,
        trial_tenants: 0,
        total_users: 0,
      },
    };
  },
  computed: {
    cards() {
      return [
        { label: 'Total Tenants', value: this.stats.total_tenants, icon: 'briefcase', tone: 'tone-orange', note: 'All companies' },
        { label: 'Active Tenants', value: this.stats.active_tenants, icon: 'check-circle', tone: 'tone-green', note: 'Currently running' },
        { label: 'Suspended Tenants', value: this.stats.suspended_tenants, icon: 'pause-circle', tone: 'tone-red', note: 'Access paused' },
        { label: 'Trial Tenants', value: this.stats.trial_tenants, icon: 'clock', tone: 'tone-blue', note: 'Evaluation plan' },
        { label: 'Total Users', value: this.stats.total_users, icon: 'users', tone: 'tone-teal', note: 'Across tenants' },
      ];
    },
    summaryItems() {
      return [
        { label: 'Activation Rate', value: this.percent(this.stats.active_tenants, this.stats.total_tenants), note: 'Active tenants out of total' },
        { label: 'Suspension Rate', value: this.percent(this.stats.suspended_tenants, this.stats.total_tenants), note: 'Tenants currently suspended' },
        { label: 'Average Users', value: this.average(this.stats.total_users, this.stats.total_tenants), note: 'Users per tenant' },
      ];
    },
    activeTenants() {
      return this.tenants.filter((tenant) => tenant.is_active);
    },
  },
  mounted() {
    this.fetchDashboard();
  },
  methods: {
    async fetchDashboard() {
      this.loading = true;
      this.error = '';
      try {
        const [stats, tenants] = await Promise.all([
          getSuperAdminDashboard(),
          listTenants(),
        ]);
        this.stats = stats;
        this.tenants = tenants || [];
      } catch (error) {
        this.error = error?.response?.data?.detail || 'Unable to load dashboard.';
      } finally {
        this.loading = false;
      }
    },
    percent(value, total) {
      if (!total) {
        return '0%';
      }
      return `${Math.round((Number(value || 0) / Number(total)) * 100)}%`;
    },
    average(value, total) {
      if (!total) {
        return '0';
      }
      return (Number(value || 0) / Number(total)).toFixed(1);
    },
    openSystemUserModal() {
      this.systemUserForm = emptySystemUserForm();
      this.tenantLocations = [];
      this.systemUserError = '';
      this.showSystemUserModal = true;
    },
    closeSystemUserModal() {
      if (this.systemUserSubmitting) {
        return;
      }
      this.showSystemUserModal = false;
    },
    async loadTenantLocations() {
      this.systemUserForm.locationid = '';
      this.tenantLocations = [];

      if (!this.systemUserForm.tenant_id) {
        return;
      }

      this.tenantLocationsLoading = true;
      this.systemUserError = '';
      try {
        const tenant = await getTenantDetails(this.systemUserForm.tenant_id);
        this.tenantLocations = (tenant.locations || []).filter((location) => location.is_active);
        const defaultLocation = this.tenantLocations.find((location) => location.is_default);
        this.systemUserForm.locationid = defaultLocation?.locationid || this.tenantLocations[0]?.locationid || '';
      } catch (error) {
        this.systemUserError = error?.response?.data?.detail || 'Unable to load tenant locations.';
      } finally {
        this.tenantLocationsLoading = false;
      }
    },
    async submitSystemUser() {
      if (this.systemUserForm.password !== this.systemUserForm.confirmPassword) {
        Swal.fire({ icon: 'error', title: 'Mismatch', text: 'Password and confirm password must match.' });
        return;
      }

      this.systemUserSubmitting = true;
      this.systemUserError = '';
      try {
        await createSystemInternalUser({
          tenant_id: this.systemUserForm.tenant_id,
          locationid: this.systemUserForm.locationid || null,
          name: this.systemUserForm.name,
          email: this.systemUserForm.email,
          password: this.systemUserForm.password,
          is_active: this.systemUserForm.is_active,
          must_change_password: this.systemUserForm.must_change_password,
        });
        this.showSystemUserModal = false;
        await this.fetchDashboard();
        Swal.fire({
          icon: 'success',
          title: 'System internal user created',
          timer: 1400,
          showConfirmButton: false,
        });
      } catch (error) {
        this.systemUserError = error?.response?.data?.detail || 'Could not create system internal user.';
      } finally {
        this.systemUserSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-kpi-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  margin-bottom: 24px;
}

.metric-card {
  align-items: center;
  border: 0;
  border-radius: 8px;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
  color: #fff;
  display: flex;
  gap: 14px;
  min-height: 112px;
  overflow: hidden;
  padding: 22px 24px;
  position: relative;
}

.metric-card::after {
  background: rgba(255, 255, 255, 0.13);
  border-radius: 50%;
  content: "";
  height: 132px;
  position: absolute;
  right: -34px;
  top: -48px;
  width: 132px;
}

.metric-icon {
  align-items: center;
  background: #fff;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 54px;
  height: 54px;
  justify-content: center;
  position: relative;
  width: 54px;
  z-index: 1;
}

.metric-icon :deep(svg),
.head-icon :deep(svg),
.quick-actions :deep(svg) {
  height: 18px;
  stroke-width: 2.4;
  width: 18px;
}

.metric-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  position: relative;
  z-index: 1;
}

.metric-label {
  color: rgba(255, 255, 255, 0.92);
  font-size: 15px;
  font-weight: 700;
}

.metric-value {
  color: #fff;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
}

.metric-note {
  align-self: flex-start;
  background: rgba(255, 255, 255, 0.94);
  border-radius: 7px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 9px;
}

.tone-orange { background: linear-gradient(135deg, #ffa73d 0%, #ff922b 100%); }
.tone-green { background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%); }
.tone-red { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
.tone-blue { background: linear-gradient(135deg, #4968f2 0%, #3155e7 100%); }
.tone-teal { background: linear-gradient(135deg, #36a892 0%, #238b79 100%); }
.tone-orange .metric-icon { color: #f97316; }
.tone-green .metric-icon { color: #16a34a; }
.tone-red .metric-icon { color: #dc2626; }
.tone-blue .metric-icon { color: #3155e7; }
.tone-teal .metric-icon { color: #0f8f7d; }

.dashboard-title {
  align-items: center;
  display: flex;
  gap: 12px;
}

.dashboard-title h4 {
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
  margin: 0;
}

.dashboard-title p {
  color: #64748b;
  font-size: 12px;
  margin: 4px 0 0;
}

.head-icon {
  align-items: center;
  background: #fff6ee;
  border-radius: 10px;
  color: #f36f21;
  display: inline-flex;
  flex: 0 0 40px;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.head-icon.warn {
  color: #d97706;
}

.summary-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.summary-item {
  background: #f8fafc;
  border: 1px solid #eef0f4;
  border-radius: 8px;
  min-height: 112px;
  padding: 14px;
}

.summary-item span,
.summary-item small {
  color: #667085;
  display: block;
  font-size: 12px;
  font-weight: 700;
}

.summary-item strong {
  color: #111827;
  display: block;
  font-size: 24px;
  font-weight: 800;
  margin: 8px 0;
}

.quick-actions {
  display: grid;
  gap: 10px;
}

.quick-actions a,
.quick-actions button {
  align-items: center;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  font-weight: 700;
  gap: 10px;
  min-height: 48px;
  padding: 12px;
  text-align: left;
}

.quick-actions button:disabled {
  opacity: 0.7;
}

.platform-card {
  align-items: flex-start;
  border: 1px solid #e8ebed;
  border-radius: 8px;
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
  min-height: 126px;
  padding: 18px;
}

.platform-card span {
  align-items: center;
  background: #fff6ee;
  border-radius: 8px;
  color: #ff9f43;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.platform-card h6 {
  color: #212b36;
  font-weight: 700;
  margin-bottom: 6px;
}

.platform-card p {
  color: #637381;
  line-height: 1.5;
  margin: 0;
}

@media (max-width: 991px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>
